<template>
  <div class="transfer" :class="variant">
    <div class="transfer-actions">
      <button
        type="button"
        class="transfer-btn"
        :disabled="!hasGame"
        @click="exportGames"
      >
        <span class="transfer-icon" aria-hidden="true">↓</span> Exporteren
      </button>
      <button
        v-if="showImport"
        type="button"
        class="transfer-btn"
        @click="pickFile"
      >
        <span class="transfer-icon" aria-hidden="true">↑</span> Importeren
      </button>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="application/json,.json"
      class="file-input"
      @change="readFile"
    />

    <p v-if="showMessage" class="transfer-msg" :class="{ error: failed }" role="status">{{ message }}</p>

    <ConfirmDialog
      v-if="pendingGames"
      title="Spelstand vervangen?"
      :body="`Het bestand bevat ${pendingSummary}. De huidige spelstand gaat verloren.`"
      confirm-label="Importeren"
      @confirm="applyImport"
      @cancel="cancelImport"
    />
  </div>
</template>

<script>
import { useGameStore } from '../../stores/game'
import { useBoerenbridgeStore } from '../../stores/boerenbridge'
import {
  GAME_KEYS,
  buildExport,
  exportFilename,
  parseExport,
  describeGames
} from '../../models/gameTransfer'
import ConfirmDialog from './ConfirmDialog.vue'

const MESSAGE_TIMEOUT = 4000

export default {
  name: 'GameTransfer',
  components: { ConfirmDialog },
  props: {
    showImport: { type: Boolean, default: true },
    variant: { type: String, default: 'block' }
  },
  data() {
    return {
      message: '',
      failed: false,
      pendingGames: null,
      messageTimer: null
    }
  },
  computed: {
    stores: () => ({ game: useGameStore(), boerenbridge: useBoerenbridgeStore() }),
    hasGame() {
      return GAME_KEYS.some(key => this.stores[key].players.length > 0)
    },
    /**
     * In de compacte variant zit er geen ruimte voor een melding naast de knop;
     * de download van de browser is daar de bevestiging.
     */
    showMessage() {
      return !!this.message && this.variant !== 'inline'
    },
    pendingSummary() {
      return this.pendingGames ? describeGames(this.pendingGames) : ''
    }
  },
  beforeUnmount() {
    clearTimeout(this.messageTimer)
  },
  methods: {
    /** Spelstand zoals die ook in sessionStorage staat, zonder tijdelijke velden. */
    snapshot(store) {
      const state = { ...store.$state }
      delete state.confirmingEndGame
      return JSON.parse(JSON.stringify(state))
    },

    exportGames() {
      if (!this.hasGame) return
      const games = Object.fromEntries(GAME_KEYS.map(key => [key, this.snapshot(this.stores[key])]))
      const blob = new Blob([JSON.stringify(buildExport(games), null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = exportFilename()
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      this.notify('Spelstand geëxporteerd.')
    },

    pickFile() {
      this.$refs.fileInput.click()
    },

    async readFile(event) {
      const file = event.target.files && event.target.files[0]
      // Leegmaken zodat hetzelfde bestand opnieuw gekozen kan worden
      event.target.value = ''
      if (!file) return

      let games
      try {
        games = parseExport(await file.text())
      } catch (e) {
        this.notify(e.message, true)
        return
      }

      if (this.hasGame) {
        this.pendingGames = games
        return
      }
      this.loadGames(games)
    },

    applyImport() {
      const games = this.pendingGames
      this.pendingGames = null
      this.loadGames(games)
    },

    cancelImport() {
      this.pendingGames = null
    },

    loadGames(games) {
      for (const key of GAME_KEYS) {
        const store = this.stores[key]
        games[key] ? store.importState(games[key]) : store.resetGame()
      }
      this.notify('Spelstand geïmporteerd.')
    },

    notify(message, failed = false) {
      this.message = message
      this.failed = failed
      clearTimeout(this.messageTimer)
      this.messageTimer = setTimeout(() => { this.message = '' }, MESSAGE_TIMEOUT)
    }
  }
}
</script>

<style scoped>
.transfer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.transfer-actions {
  display: flex;
  gap: 0.5rem;
  width: 100%;
}

.transfer-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: #2c3e50;
  background: transparent;
  border: 2px solid #aac8aa;
  border-radius: 8px;
  cursor: pointer;
  touch-action: manipulation;
}

.transfer-btn:disabled {
  color: #a8b8a8;
  border-color: #cddccd;
  cursor: not-allowed;
}

.transfer-btn:active:not(:disabled) { opacity: 0.75; }

.transfer-icon {
  font-size: 1rem;
  line-height: 1;
}

.file-input {
  display: none;
}

.transfer-msg {
  font-size: 0.8rem;
  color: #5a6d5a;
  text-align: center;
}

.transfer-msg.error {
  color: #c0392b;
  font-weight: 600;
}

/* Compacte variant voor in een scorebord-kop */
.transfer.inline {
  width: auto;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}

.transfer.inline .transfer-actions {
  width: auto;
}

.transfer.inline .transfer-btn {
  flex: 0 0 auto;
  padding: 0.3rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 600;
  border-width: 1px;
  background: #fff;
}

.transfer.inline .transfer-icon { font-size: 0.8rem; }

@media (hover: hover) {
  .transfer-btn:hover:not(:disabled) {
    border-color: #7a9a7a;
    background: rgba(255, 255, 255, 0.5);
  }
}
</style>
